/**
 * RenderSubsystem - Rendering for the player runtime
 *
 * Architecture:
 *   GameViewRenderer (from @engine/core) handles everything:
 *     - Sprite rendering with UV-flip, sorting, pivot, tint
 *     - Camera2D component integration (ortho size, bg colour, post-processing)
 *     - Async texture loading via textureResolver callback
 *     - Deferred lighting / Forward+ / VFX / Shadows (callback-driven)
 *
 * The subsystem is a thin wrapper that:
 *   1. Creates and initialises a GameViewRenderer
 *   2. Wires a texture resolver from AssetSubsystem manifest
 *   3. Delegates renderFrame() → GameViewRenderer.render()
 */

import {
  Subsystem,
  type SubsystemConfig,
  SubsystemPriority,
  GameViewRenderer,
  type Level,
  type CameraInstance,
  type ViewportRenderRequest,
  collectLightsFromLevel,
  collectShadowCastersFromLevel,
} from '@engine/core';

import {
  LightManager,
  DeferredLightingPipeline,
  IESProfileManager,
  IES_PRESETS,
  registerCompiledShaders,
  compiledShaderNames,
  MaterialCache,
  type ResolvedMaterial,
} from '@engine/render2d';

import type { AssetSubsystem } from './AssetSubsystem';

// ============================================================================
// OPTIONS
// ============================================================================

export interface RenderSubsystemOptions {
  canvas: HTMLCanvasElement;
}

// ============================================================================
// SUBSYSTEM
// ============================================================================

export class RenderSubsystem extends Subsystem {
  readonly config: SubsystemConfig = {
    name: 'render',
    priority: SubsystemPriority.Core + 20,
    optional: false,
    dependencies: ['assets'],
  };

  private readonly options: RenderSubsystemOptions;

  // Core renderer (shared between editor and player)
  private renderer: GameViewRenderer | null = null;

  // Lighting
  private lightManager: LightManager | null = null;
  private iesProfileManager: IESProfileManager | null = null;
  private deferredPipeline: DeferredLightingPipeline | null = null;

  // Materials — the same shared cache the editor uses, fed precompiled {data, wgsl, reflection} from the build.
  private materialCache: MaterialCache | null = null;

  // State
  private level: Level | null = null;
  private _activeCameraEntity: number | null = null;
  private time = 0;
  private lastDt = 1 / 60;

  constructor(options: RenderSubsystemOptions) {
    super();
    this.options = options;
  }

  // ==========================================================================
  // ACCESSORS
  // ==========================================================================

  getContext() {
    return this.renderer?.getContext() ?? null;
  }

  /** The underlying shared renderer — external passes (e.g. UIPass) are injected via addPass. */
  get gameRenderer(): GameViewRenderer | null {
    return this.renderer;
  }

  get backend(): string {
    return this.renderer?.getContext()?.backend ?? 'unknown';
  }

  // ==========================================================================
  // LIFECYCLE
  // ==========================================================================

  async onInitialize(): Promise<void> {
    const { canvas } = this.options;

    const assets = this.getSubsystem<AssetSubsystem>('assets');
    const manifest = assets.getManifest();

    // Register the engine's compiled WGSL shaders into render2d's ShaderCache BEFORE the renderer
    // initializes — SpriteBatch + pipelines read getCompiledShader() during GameViewRenderer.initialize().
    // The shaders are authored in Slang and compiled to WGSL by the build (ExportShadersStep); the player
    // can't run slang, so it just registers the shipped map. Without this the renderer throws
    // "ShaderCache: 'sprite' not compiled" on startup.
    await this.registerEngineShaders(assets, manifest);

    this.renderer = new GameViewRenderer('webgpu');
    await this.renderer.initialize(canvas);
    this.renderer.setPlayMode(true);

    // Wire texture resolver via AssetSubsystem
    this.renderer.setTextureResolver(async (spriteId: string) => {
      const path = manifest?.assets.textures?.[spriteId];
      if (!path) return null;
      return assets.resolveUrl(path);
    });

    // ── Lighting + Shadows ──
    const ctx = this.renderer.getContext()!;

    // Create IES profile manager and register all built-in presets
    this.iesProfileManager = new IESProfileManager(ctx);
    for (const preset of IES_PRESETS) {
      this.iesProfileManager.addPreset(preset);
    }
    this.iesProfileManager.upload();

    // Create light manager and link IES profile manager
    this.lightManager = new LightManager(ctx);
    this.lightManager.iesProfileManager = this.iesProfileManager;

    // Deferred lighting callback (GBuffer path)
    this.renderer.setDeferredLightingCallback((w, h) => {
      if (!this.lightManager) return null;
      if (!this.deferredPipeline) {
        try {
          this.deferredPipeline = new DeferredLightingPipeline(ctx, w, h);
        } catch {
          return null;
        }
      } else {
        this.deferredPipeline.resize(w, h);
      }
      return {
        pipeline: this.deferredPipeline,
        lightManager: this.lightManager,
        collect: (cam, cw, ch) => {
          if (this.level && this.lightManager) {
            collectLightsFromLevel(this.level, this.lightManager, cam, cw, ch);
          }
        },
        iesProfileBuffer: this.iesProfileManager?.iesBuffer ?? null,
      };
    });

    // Forward+ lighting collect callback
    this.renderer.setLightingCollectCallback((cam, w, h) => {
      if (this.level && this.lightManager) {
        collectLightsFromLevel(this.level, this.lightManager, cam, w, h);
      }
    });

    // Shadow caster callback
    this.renderer.setShadowCasterCollectCallback(() => {
      if (!this.level) return [];
      return collectShadowCastersFromLevel(this.level);
    });

    // ── VFX Callbacks ──
    // Lazy-resolve VFXSubsystem (it registers after render)
    let vfxSub: import('./VFXSubsystem').VFXSubsystem | null | undefined;
    const getVFX = () => {
      if (vfxSub === undefined) {
        vfxSub = this.getSubsystem<import('./VFXSubsystem').VFXSubsystem>('vfx') ?? null;
      }
      return vfxSub;
    };

    this.renderer.setVFXRenderCallback((renderCtx, viewProjection, isHdrTarget) => {
      getVFX()?.render(viewProjection, renderCtx, isHdrTarget);
    });

    this.renderer.setVFXLightInjectCallback(() => {
      return getVFX()?.getLightEmittingSystems() ?? [];
    });

    // ── Materials ──
    await this.setupMaterials(assets, manifest);

    console.log('[RenderSubsystem] Initialized via GameViewRenderer (lighting + shadows + VFX + materials wired)');
  }

  /**
   * Load the build's compiled materials map ({guid: {data, wgsl, reflection}}) and wire a {@link MaterialCache}
   * into the renderer's material resolver — the same cache the editor uses, just resolving from precompiled
   * build output instead of live slang-wasm. Without this, material sprites render plain (no shader) in the player.
   */
  private async setupMaterials(
    assets: AssetSubsystem,
    manifest: ReturnType<AssetSubsystem['getManifest']>,
  ): Promise<void> {
    const matPath = manifest?.materials;
    if (!matPath) {
      console.warn('[RenderSubsystem] manifest.materials missing — material sprites will render plain.');
      return;
    }
    const ctx = this.renderer?.getContext();
    if (!ctx || !this.renderer) return;

    const materials = await assets.loadJson<Record<string, ResolvedMaterial>>('__materials__', matPath);
    this.materialCache = new MaterialCache({
      ctx,
      resolve: async (guid) => materials[guid] ?? null,
      // NOTE: `.mat`-assigned textures aren't wired in the player yet (async URL→handle vs the sync resolver);
      // unassigned slots get the white default, and the sprite's own texture is bound as ALBEDO by the renderer.
      resolveTexture: () => null,
    });
    this.renderer.setMaterialResolver((guid) => this.materialCache!.get(guid));
    console.log(`[RenderSubsystem] Materials ready (${Object.keys(materials).length})`);
  }

  /**
   * Load the build's compiled WGSL shader map (assets/shaders.json) and register it into render2d's
   * ShaderCache. Idempotent across level reloads (registry is last-wins). Skips silently if already
   * populated (e.g. editor in-play-mode shares the cache the editor warmed at startup).
   */
  private async registerEngineShaders(
    assets: AssetSubsystem,
    manifest: ReturnType<AssetSubsystem['getManifest']>,
  ): Promise<void> {
    if (compiledShaderNames().length > 0) return;

    const shadersPath = manifest?.shaders;
    if (!shadersPath) {
      console.warn(
        '[RenderSubsystem] manifest.shaders missing — engine shaders not shipped by the build ' +
          '(ExportShadersStep). Rendering will fail.',
      );
      return;
    }

    const shaders = await assets.loadJson<Record<string, string>>('__engine_shaders__', shadersPath);
    registerCompiledShaders(shaders);
    console.log(`[RenderSubsystem] Registered ${Object.keys(shaders).length} compiled shader(s)`);
  }

  onUpdate(dt: number): void {
    this.time += dt;
    this.lastDt = dt;
  }

  onShutdown(): void {
    this.materialCache?.dispose();
    this.materialCache = null;
    this.deferredPipeline?.destroy();
    this.deferredPipeline = null;
    this.lightManager?.destroy();
    this.lightManager = null;
    this.iesProfileManager?.destroy();
    this.iesProfileManager = null;
    this.renderer?.dispose();
    this.renderer = null;
    this.level = null;
  }

  // ==========================================================================
  // PUBLIC API
  // ==========================================================================

  /** Set the current level to render */
  setLevel(level: Level | null): void {
    this.level = level;
    if (!level || !this.renderer) return;

    // Find first Camera2D entity and set it as the active camera
    for (const go of level.getAllObjects()) {
      const camData = go.getComponentData('core.Camera2D');
      if (camData) {
        this.setActiveCameraByEntity(go.id);
        break;
      }
    }
  }

  /** The entity whose Camera2D is currently active/live (or null). */
  get activeCameraEntity(): number | null {
    return this._activeCameraEntity;
  }

  /** Make a specific entity's Camera2D the active/live camera at runtime (engine.camera.setActive). */
  setActiveCameraByEntity(entityId: number): void {
    if (!this.renderer) return;
    this.renderer.setActiveCamera({
      entityId,
      transformHandle: 0,
      postProcessStack: { enabled: true, effects: [] },
      initialized: true,
      lastRenderTime: 0,
    } as CameraInstance);
    this._activeCameraEntity = entityId;
  }

  /**
   * Set background color (fallback if no Camera2D in the level).
   * GameViewRenderer reads background color from Camera2D component automatically.
   */
  setBackgroundColor(r: number, g: number, b: number): void {
    if (!this.renderer) return;
    this.renderer.setBackgroundColor(
      (Math.round(r * 255) << 16) |
      (Math.round(g * 255) << 8) |
      Math.round(b * 255),
    );
  }

  /**
   * @deprecated Camera state is now driven by Camera2D component + CameraRig.
   * Kept for backward compatibility with LevelSubsystem.applyCameraSettings.
   */
  setCamera(_state: { x: number; y: number; zoom: number; rotation: number }): void {
    // No-op: GameViewRenderer reads camera from the Camera2D entity transform
  }

  /**
   * Register a pre-loaded texture.
   * With GameViewRenderer, textures are loaded lazily via the textureResolver.
   * This method pre-warms the internal cache by resolving the texture immediately.
   */
  registerTexture(id: string, _image: HTMLImageElement): void {
    // GameViewRenderer handles texture loading internally via textureResolver.
    // Trigger an eager resolve to pre-warm the cache.
    this.renderer?.resolveTextureToHandle(id);
  }

  /**
   * Acquire (warm) a texture into the renderer's GPU cache. Awaitable so the level lifecycle can
   * preload a level's textures off the critical path before activating it.
   */
  async acquireTexture(id: string): Promise<void> {
    await this.renderer?.resolveTextureToHandle(id);
  }

  /**
   * Release a texture's GPU resource (no longer referenced by any loaded level). Called by the
   * level lifecycle's ResourceManager on unload.
   */
  releaseTexture(id: string): void {
    this.renderer?.releaseTexture(id);
  }

  /** Check if a texture is already loaded */
  getTexture(id: string): unknown {
    // We can't easily query GameViewRenderer's internal cache.
    // Return a truthy value if we've attempted to preload it.
    return null;
  }

  /** Render one frame — called from the bridge ISystem's render callback */
  renderFrame(): void {
    if (!this.renderer || !this.renderer.isInitialized()) return;

    const ctx = this.renderer.getContext();
    if (!ctx) return;

    const { canvas } = this.options;

    const request: ViewportRenderRequest = {
      ctx,
      width: canvas.width,
      height: canvas.height,
      camera: { x: 0, y: 0, zoom: 1, rotation: 0 },
      level: this.level,
      selectedGuids: new Set(),
      time: this.time,
      deltaTime: this.lastDt,
      isPlayMode: true,
    };

    this.renderer.render(request);
  }

  /** Resize viewport */
  resize(width: number, height: number): void {
    this.renderer?.resize(width, height);
  }
}

